import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
  model,
  signal,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-password',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiPassword),
      multi: true,
    },
  ],
  template: `
    <div class="relative w-full">
      <input
        [id]="inputId()"
        [type]="showPassword() ? 'text' : 'password'"
        [placeholder]="promptLabel()"
        [disabled]="disabled()"
        [autocomplete]="autocomplete()"
        [class]="inputClasses()"
        [value]="value()"
        (input)="onInput($event)"
        (blur)="onTouched()"
        [attr.aria-label]="ariaLabel() || promptLabel()"
        [attr.aria-invalid]="invalid()"
        [attr.aria-describedby]="feedback() ? inputId() + '-strength' : null"
      />
      @if (toggleMask()) {
        <button
          type="button"
          class="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
          (click)="toggleVisibility()"
          [attr.aria-label]="showPassword() ? 'Ocultar contraseña' : 'Mostrar contraseña'"
        >
          <i [class]="showPassword() ? 'bi bi-eye-slash' : 'bi bi-eye'" aria-hidden="true"></i>
        </button>
      }
    </div>

    @if (feedback() && value()) {
      <div [id]="inputId() + '-strength'" class="mt-2" role="status" aria-live="polite">
        <div class="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
          <div
            class="h-full rounded-full transition-all duration-300"
            [class]="strengthBarClass()"
            [style.width]="strengthPercent() + '%'"
          ></div>
        </div>
        <p class="text-xs mt-1" [class]="strengthTextClass()">{{ strengthLabel() }}</p>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UiPassword {
  readonly promptLabel = input<string>('Ingrese contraseña');
  readonly weakLabel = input<string>('Débil');
  readonly mediumLabel = input<string>('Media');
  readonly strongLabel = input<string>('Fuerte');
  readonly toggleMask = input<boolean>(false);
  readonly feedback = input<boolean>(true);
  readonly disabled = input<boolean>(false);
  readonly invalid = input<boolean>(false);
  readonly autocomplete = input<string>('off');
  readonly pattern = input<string>('');
  readonly inputId = input<string>('password-' + Math.random().toString(36).substring(2, 9));
  readonly ariaLabel = input<string>('');
  readonly styleClass = input<string>('');

  readonly value = model<string>('');

  protected readonly showPassword = signal(false);

  onTouched: () => void = () => {};
  private onChange: (value: string) => void = () => {};

  protected readonly inputClasses = computed(() => {
    const base =
      'w-full px-3 py-2 pr-10 text-sm border rounded-lg transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-accent-500/20 focus:border-accent-500 placeholder:text-slate-400';
    const normal = 'border-slate-200 hover:border-slate-300 bg-white';
    const invalidCls = 'border-red-400 focus:ring-red-500/20 focus:border-red-500 bg-white';
    const disabled = this.disabled() ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : '';
    return `${base} ${this.invalid() ? invalidCls : normal} ${disabled} ${this.styleClass()}`.trim();
  });

  protected readonly strength = computed(() => {
    const val = this.value();
    if (!val) return 0;
    let score = 0;
    if (val.length >= 8) score++;
    if (/[a-z]/.test(val) && /[A-Z]/.test(val)) score++;
    if (/\d/.test(val)) score++;
    if (/[^A-Za-z0-9]/.test(val)) score++;
    return score;
  });

  protected readonly strengthPercent = computed(() => (this.strength() / 4) * 100);

  protected readonly strengthLabel = computed(() => {
    const s = this.strength();
    if (s <= 1) return this.weakLabel();
    if (s <= 3) return this.mediumLabel();
    return this.strongLabel();
  });

  protected readonly strengthBarClass = computed(() => {
    const s = this.strength();
    if (s <= 1) return 'bg-red-500';
    if (s <= 3) return 'bg-amber-500';
    return 'bg-green-500';
  });

  protected readonly strengthTextClass = computed(() => {
    const s = this.strength();
    if (s <= 1) return 'text-red-600';
    if (s <= 3) return 'text-amber-600';
    return 'text-green-600';
  });

  protected toggleVisibility(): void {
    this.showPassword.update((v) => !v);
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.value.set(target.value);
    this.onChange(target.value);
  }

  writeValue(value: string): void {
    this.value.set(value || '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(): void {
    // handled via input
  }
}
